import { getFaqsByPage } from "lib/payloadApi";
import { useRouter } from "next/router";
import { useToast } from "@chakra-ui/react";
import SearchField from "components/blocks/SearchField";
import { useEffect, useRef, useState } from "react";
import MainContainer from "components/sections/MainContainer";
import TextHeader from "components/sections/TextHeader";
import FaqSection from "components/sections/FaqSection";
import MetaHead from "components/base/MetaHead";
import Trans, { useFormatMessage } from "components/base/Trans";
import Text from "components/base/Text";
import { allowedLocales } from "translations";
import { MotionBox as Box } from "utils/motion";

export const loadingVariant = {
  normal: { opacity: 1 },
  loading: { opacity: 0.6, transition: { type: "tween", duration: 0.1 } },
};

const getDefaultIndex = (queryId, items) =>
  items.findIndex((item) => item.uid === queryId);

export default function Faqs({ initialFaqs, faqSchema }) {
  const formatMessage = useFormatMessage();
  const router = useRouter();
  const urlId = router.query.id;
  const [loading, setLoading] = useState(false);
  const [faqs, setFaqs] = useState(initialFaqs);
  const [searchToken, setSearchToken] = useState();
  const [filteredFaqs, setFilteredFaqs] = useState(faqs);
  const toast = useToast();
  const linkCopyMsg = formatMessage({ id: "LBL_URL_COPIED" });
  const searchDebounceRef = useRef(null);
  const isInitialDeepLink = useRef(true);
  const handleChange = (text) => {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchToken(text.toLowerCase());
    }, 200);
  };

  useEffect(() => {
    const selectedLocale =
      allowedLocales.indexOf(router.locale) > -1 ? router.locale : null;

    getFaqsByPage("faq", selectedLocale).then((faqList) => {
      setFaqs(faqList);
    });
  }, [router.locale]);

  useEffect(() => {
    if (searchToken) {
      const filtered = faqs
        .map((cat) => ({
          ...cat,
          faqs: cat.faqs.filter(
            (faq) =>
              faq.category.toLowerCase().includes(searchToken) ||
              faq.answerText.toLowerCase().includes(searchToken) ||
              faq.question.toLowerCase().includes(searchToken),
          ),
        }))
        .filter((cat) => cat.faqs.length > 0);

      setFilteredFaqs(filtered);
      setLoading(true);
      setTimeout(() => setLoading(false), 600);
    } else {
      setFilteredFaqs(faqs);
    }
  }, [faqs, searchToken]);

  useEffect(() => {
    if (!isInitialDeepLink.current) return;
    if (urlId) {
      const element = document.getElementById(urlId);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 500);

        setTimeout(() => {
          const accordionButton = element.querySelector(
            ".chakra-accordion__button",
          );
          if (accordionButton) {
            accordionButton.click();
          }
        }, 200);
      }
    }
  }, [urlId]);

  const onFaqClick = (queyId) => {
    isInitialDeepLink.current = false;
    router.push(`faq?id=${queyId}`, `faq?id=${queyId}`, { shallow: true });
  };

  const onLinkClick = (queryId) => {
    navigator.clipboard
      .writeText(`https://www.hillhelicopters.com/faq?id=${queryId}`)
      .then(() => {
        toast({
          title: linkCopyMsg,
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      });
  };

  return (
    <MainContainer footerSmall navStyle={{ gradient: true, inverted: false }}>
      <MetaHead title="LBL_FAQ_META_TITLE" description="LBL_FAQ_DESC" structuredData={faqSchema} />
      <TextHeader
        inverted
        title={<Trans id="LBL_FAQ_TITLE" />}
        page={<Trans id="LBL_FAQ_SUPPORT" />}
      />
      <SearchField onChange={handleChange} loading={loading} />
      <Box pb={["md", null, "xl"]} bg="black.700">
        <Box
          variants={loadingVariant}
          initial="normal"
          animate={loading ? "loading" : "normal"}
        >
          {filteredFaqs.map(({ categoryTitle, slug, faqs: categoryFaqs }) => (
            <div id={slug} key={slug}>
              <FaqSection
                noSubtitle
                pb={["md", null, "lg"]}
                items={categoryFaqs}
                title={categoryTitle}
                defaultIndex={getDefaultIndex(urlId, categoryFaqs)}
                onClick={onFaqClick}
                onLinkClick={onLinkClick}
              />
            </div>
          ))}
          {filteredFaqs.length === 0 && (
            <Text type="headline_md" color="primary" align="center" py="xl">
              <Trans id="LBL_NO_RESULTS_FOUND" />
            </Text>
          )}
        </Box>
      </Box>
    </MainContainer>
  );
}

export async function getStaticProps(ctx) {
  const selectedLocale =
    allowedLocales.indexOf(ctx.locale) > -1 ? ctx.locale : null;

  const faqCategories = await getFaqsByPage("faq", selectedLocale);

  const allFaqs = faqCategories.flatMap((cat) => cat.faqs);

  const faqSchemaEntities = allFaqs
    .filter((faq) => faq.question && faq.answerText)
    .map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answerText,
      },
    }));

  const faqSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqSchemaEntities,
  });

  return {
    props: { initialFaqs: faqCategories, faqSchema },
    revalidate: 60,
  };
}
